// src/components/question/MyNotebook.tsx
// ✅ CORRIGIDO:
//   - passa allQuestionIds para QuestionContainer (mostra só questões salvas)
//   - sem resetar currentQuestionId a cada re-render de loadSavedQuestions

import React, { useState, useEffect, useCallback } from 'react';
import { BookMarked, Search, Trash2, X } from 'lucide-react';
import { savedQuestionService } from '../../services/savedQuestionService';
import { questionService } from '../../services/questionService';
import QuestionContainer from './QuestionContainer';
import QuestionFilters from './QuestionFilters';
import { QuestionFilters as Filters } from '../../types/question.types';
import './MyNotebook.css';

interface MyNotebookProps {
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
    onRegisterOpenGrid?: (fn: () => void) => void;
}

const MyNotebook: React.FC<MyNotebookProps> = ({ onShowToast, onRegisterOpenGrid }) => {
    const [currentQuestionId, setCurrentQuestionId] = useState<string | null>(null);
    const [savedQuestionIds, setSavedQuestionIds] = useState<string[]>([]);
    const [filters, setFilters] = useState<Filters>({});
    const [loading, setLoading] = useState(true);
    const [showGridModal, setShowGridModal] = useState(false);

    useEffect(() => {
        onRegisterOpenGrid?.(() => setShowGridModal(true));
    }, [onRegisterOpenGrid]);

    const loadSavedQuestions = useCallback(async () => {
        try {
            setLoading(true);

            const data = await savedQuestionService.getSavedQuestions({
                category: filters.level1,
                limit: 1000
            });

            const ids = data.items.map((item: any) => item.question._id);
            setSavedQuestionIds(ids);

            // ✅ Só define a primeira questão se ainda não há nenhuma selecionada
            // ou se a questão atual não está mais na lista filtrada
            setCurrentQuestionId(prev => {
                if (ids.length === 0) return null;
                if (prev && ids.includes(prev)) return prev; // mantém a atual
                return ids[0]; // vai para a primeira
            });

        } catch (error) {
            console.error('Erro ao carregar questões salvas:', error);
            onShowToast('Erro ao carregar caderno', 'error');
        } finally {
            setLoading(false);
        }
    }, [filters, onShowToast]);

    useEffect(() => {
        loadSavedQuestions();
    }, [loadSavedQuestions]);

    return (
        <div className="my-notebook-page">

            <QuestionFilters
                onFiltersChange={setFilters}
                initialFilters={filters}
            />

            {loading ? (
                <div className="notebook-loading">
                    <div className="spinner"></div>
                    <p>Carregando caderno...</p>
                </div>
            ) : savedQuestionIds.length === 0 ? (
                <div className="notebook-empty">
                    <BookMarked size={64} />
                    <h3>Seu caderno está vazio</h3>
                    <p>Salve questões importantes para revisar depois</p>
                </div>
            ) : currentQuestionId ? (
                <div className="notebook-content">
                    {/* ✅ allQuestionIds passado — navegação só entre questões salvas */}
                    <QuestionContainer
                        questionId={currentQuestionId}
                        onQuestionChange={setCurrentQuestionId}
                        onShowToast={onShowToast}
                        allQuestionIds={savedQuestionIds}
                        showPageHeader={false}
                    />
                </div>
            ) : null}

            {showGridModal && (
                <NotebookGridModal
                    savedQuestionIds={savedQuestionIds}
                    onClose={() => setShowGridModal(false)}
                    onSelectQuestion={(id) => {
                        setCurrentQuestionId(id);
                        setShowGridModal(false);
                    }}
                    onRemoveQuestion={async (id) => {
                        try {
                            await savedQuestionService.removeQuestion(id);
                            onShowToast('Questão removida', 'success');
                            loadSavedQuestions();
                        } catch {
                            onShowToast('Erro ao remover', 'error');
                        }
                    }}
                    onShowToast={onShowToast}
                />
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────
// MODAL GRID
// ─────────────────────────────────────────────────────────────
interface NotebookGridModalProps {
    savedQuestionIds: string[];
    onClose: () => void;
    onSelectQuestion: (id: string) => void;
    onRemoveQuestion: (id: string) => void;
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const NotebookGridModal: React.FC<NotebookGridModalProps> = ({
    savedQuestionIds,
    onClose,
    onSelectQuestion,
    onRemoveQuestion
}) => {
    const [questions, setQuestions] = useState<any[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [loading, setLoading] = useState(true);

    const loadQuestions = useCallback(async () => {
        try {
            setLoading(true);
            const questionsData = await Promise.all(
                savedQuestionIds.map(id =>
                    questionService.getQuestion(id).then((d: any) => d.question).catch(() => null)
                )
            );
            setQuestions(questionsData.filter(Boolean));
        } catch (error) {
            console.error('Erro ao carregar questões:', error);
        } finally {
            setLoading(false);
        }
    }, [savedQuestionIds]);

    useEffect(() => {
        loadQuestions();
    }, [loadQuestions]);

    const filteredQuestions = questions.filter(q => {
        const matchesSearch = q.statement.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = !categoryFilter || q.category.level1 === categoryFilter;
        return matchesSearch && matchesCategory;
    });

    const uniqueCategories = [...new Set(questions.map(q => q.category.level1))];

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content grid-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Meu Caderno ({questions.length} questões)</h3>
                    <button onClick={onClose}><X size={20} /></button>
                </div>

                <div className="grid-modal-filters">
                    <div className="search-input-container">
                        <Search size={18} />
                        <input
                            type="text"
                            placeholder="Buscar questões..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <select
                        value={categoryFilter}
                        onChange={e => setCategoryFilter(e.target.value)}
                        className="category-filter-select"
                    >
                        <option value="">Todas as categorias</option>
                        {uniqueCategories.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                        ))}
                    </select>
                </div>

                <div className="modal-body grid-container">
                    {loading ? (
                        <div className="grid-loading">
                            <div className="spinner"></div>
                            <p>Carregando...</p>
                        </div>
                    ) : filteredQuestions.length === 0 ? (
                        <div className="grid-empty"><p>Nenhuma questão encontrada</p></div>
                    ) : (
                        <div className="questions-grid">
                            {filteredQuestions.map(q => (
                                <div key={q._id} className="grid-question-card">
                                    <div className="grid-card-content" onClick={() => onSelectQuestion(q._id)}>
                                        <div className="grid-card-category">{q.category.level1}</div>
                                        <div className="grid-card-statement">
                                            {q.statement.replace(/<[^>]*>/g, '').substring(0, 120)}...
                                        </div>
                                        <div className={`grid-card-difficulty difficulty-${q.difficulty}`}>
                                            {q.difficulty}
                                        </div>
                                    </div>
                                    <button
                                        className="grid-card-delete"
                                        onClick={e => {
                                            e.stopPropagation();
                                            if (window.confirm('Remover esta questão?')) onRemoveQuestion(q._id);
                                        }}
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MyNotebook;